import {useAbility} from '@casl/react';
import {Resource} from '@raison/types';
import React, {useEffect, useMemo, useReducer, useState} from 'react';
import {Location, Navigate, useLocation} from 'react-router';
import {
    AddressV10nPendingInfo,
    CommonLoaderDialog,
    FeatureDeniedSupportPrompt,
    GenericErrorMsg,
    PromptToCompleteKyc,
    PromptToConfirmAddress,
} from '~/entities/dialogs';
import {selectAddressStatus, selectIsUserOnBoard} from '~/entities/user';
import {GateDialogController, GateProps, GateStep} from '~/library/navigation';
import {Wish} from '~/library/prelude';
import {RsnButton, RsnCheckbox, RsnDialog} from '~/library/ui-kit';
import {PageType} from '~/redux/access-control/selectUserAbility';
import {AppAbility, AppAbilityContext} from '~/root/access-control/AbilityContainer';
import {appLocalStorage} from '~/shared/persistence';
import {useAppSelector} from '~/shared/store/hooks';

function createAbilityGate({type, redirectPath}: {redirectPath: string; type: PageType}) {
    function AbilityGate({pass}: GateProps) {
        const ability = useAbility(AppAbilityContext);
        if (ability.can('view', type)) {
            return pass();
        } else {
            return <Navigate replace to={redirectPath} />;
        }
    }
    if (import.meta.env.DEV) {
        Object.defineProperty(AbilityGate, 'name', {
            value: `AbilityGate.${type}`,
        });
    }
    return AbilityGate;
}

export const PreviewGate = createAbilityGate({type: 'preview', redirectPath: '/'});
export const InverseAuthGate = createAbilityGate({type: 'auth', redirectPath: '/assets'});
export const KycGate = createAbilityGate({type: 'kyc', redirectPath: '/assets'});

function redirectFromRoot(location: Location, ability: AppAbility): Partial<Location> | null {
    if (ability.can('view', 'auth')) {
        return redirectFromWelcome(location);
    }
    return {pathname: '/assets'};
}

const isGoToWelcomeScreen = Wish.canParse(
    Wish.struct({
        goToWelcomeScreen: Wish.literal(true),
    })
);

function redirectFromWelcome(location: Location): Partial<Location> | null {
    const username = appLocalStorage.username.get();
    const givenName = appLocalStorage.givenName.get();
    const authStatus = appLocalStorage.authStatus.get();

    if (isGoToWelcomeScreen(location.state)) {
        return null;
    }

    if (givenName && authStatus === 'on_board') {
        return {pathname: '/login'};
    }

    if (authStatus && authStatus !== 'on_board' && username && !givenName) {
        return {pathname: '/login', state: {showLoginOneTimeCode: true}};
    }
    return null;
}

export function RootGate({pass}: GateProps) {
    const ability = useAbility(AppAbilityContext);
    const location = useLocation();
    const redirect = useMemo(() => {
        return redirectFromRoot(location, ability);
    }, [location.key, ability]);

    if (!redirect) {
        return pass();
    } else {
        return <Navigate replace to={redirect} state={redirect.state} />;
    }
}

export function FeatureGate({pass, deny, step}: GateProps) {
    const isOnBoard = useAppSelector(selectIsUserOnBoard);
    if (isOnBoard) {
        return pass();
    }
    if (step !== GateStep.Entry) {
        return <Navigate replace to='/' />;
    }

    return (
        <GateDialogController deny={deny}>
            <PromptToCompleteKyc.Dialog />
        </GateDialogController>
    );
}

export function AddressBasedGate({pass, deny, step}: GateProps) {
    const address = useAppSelector(selectAddressStatusAndDialog);

    if (step === GateStep.Entry && address.status !== 'success') {
        return <GateDialogController deny={deny} children={<address.Dialog />} />;
    }
    return pass();
}

export function createAvailabilityBasedGate(operationSelector: (state: AppModel) => Resource<unknown[]>) {
    function AvailabilityGate({pass, deny, step}: GateProps) {
        const operations = useAppSelector(operationSelector);
        const address = useAppSelector(selectAddressStatusAndDialog);

        if (step !== GateStep.Entry) {
            return pass();
        }

        const dialog = (() => {
            if (operations.isLoading || operations.isUninitialized) {
                return <CommonLoaderDialog reason='Checking access...' />;
            }
            if (operations.isError) {
                return <GenericErrorMsg.Dialog />;
            }
            if (operations.data.length === 0) {
                return <address.Dialog />;
            }
            return null;
        })();

        if (!dialog) {
            return pass();
        }
        return <GateDialogController deny={deny}>{dialog}</GateDialogController>;
    }

    if (import.meta.env.DEV) {
        Object.defineProperty(AvailabilityGate, 'name', {
            value: `AvailabilityGate.${operationSelector.name}`,
        });
    }

    return AvailabilityGate;
}

const selectAddressStatusAndDialog = (state: AppModel) => {
    const addressStatus = selectAddressStatus(state);
    switch (addressStatus) {
        case 'success':
        case null:
            return {...FeatureDeniedSupportPrompt, status: addressStatus};
        case '':
            return {...PromptToConfirmAddress, status: addressStatus};
        case 'pending':
            return {...AddressV10nPendingInfo, status: addressStatus};
    }
};

export function EthFeeWarningGate({step, pass, deny}: GateProps) {
    const [isOpened, setOpened] = useState(() => {
        const wasChecked = appLocalStorage.checkboxEthUsageAlertSeen.get() || false;
        return !wasChecked;
    });
    if (step !== GateStep.Entry) {
        return pass();
    }
    if (!isOpened) {
        return pass();
    }
    return (
        <GateDialogController deny={deny}>
            <EthFeeWarningDialog onContinue={() => setOpened(false)} />
        </GateDialogController>
    );
}

function EthFeeWarningDialog({onContinue}: {onContinue?: () => void}) {
    const [checked, toggleChecked] = useReducer((current: boolean) => !current, false);

    useEffect(() => {
        appLocalStorage.checkboxEthUsageAlertSeen.set(checked);
    }, [checked]);

    const title = 'Warning:\nLimited functionality';
    const details =
        'Without Ethereum on your Raison account, you won’t be able to exchange or send the stablecoin. It is needed to pay for blockchain network fees.';

    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader icon={{element: <RsnDialog.StatusIcon status='attention' />}}>
                {title}
            </RsnDialog.CenteredHeader>
            <RsnDialog.Body align='left'>
                <div className='fv-text-l rounded-lg bg-neutral-20 p-5'>
                    <p>{details}</p>
                    <hr className='my-5 h-px bg-neutral-30' />
                    <label className='fv-text-m-regular flex items-center gap-[0.5rem]'>
                        <RsnCheckbox onChange={toggleChecked} checked={checked} />
                        {"Don't show this message again"}
                    </label>
                </div>
            </RsnDialog.Body>
            <RsnDialog.Footer>
                <RsnButton.Primary onClick={onContinue}>Continue</RsnButton.Primary>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}
